import { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'

import api from 'repositories/api'
import { buildStoreSelectionRows, StoreSelectionRow } from '../domain/storeEligibility'
import { BulkGoalGroup, BulkStore } from '../types'
import { compareStoreNames } from '../utils'

interface UseBulkGoalStoresProps {
  selectedMonth: string
}

export const useBulkGoalStores = ({ selectedMonth }: UseBulkGoalStoresProps) => {
  const [selectedStores, setSelectedStores] = useState<string[]>([])
  const [stores, setStores] = useState<BulkStore[]>([])
  const [storesLoading, setStoresLoading] = useState(true)
  const [storesError, setStoresError] = useState<string | null>(null)
  const [groups, setGroups] = useState<BulkGoalGroup[]>([])
  const [groupsLoading, setGroupsLoading] = useState(true)
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false)
  const [hasLeftStep0, setHasLeftStep0] = useState(false)
  const availabilityRequestIdRef = useRef(0)
  const shouldScrollAfterSelectAll = useRef(false)

  const selectedStoreIds = useMemo(() => new Set(selectedStores), [selectedStores])
  const storeIdsKey = useMemo(() => stores.map(store => store._id).sort().join('|'), [stores])
  const storeRows = useMemo(() => {
    const rows = buildStoreSelectionRows(stores, { hasSelectedMonth: Boolean(selectedMonth) })

    return [...rows].sort((firstStore, secondStore) =>
      compareStoreNames(firstStore.store.name, secondStore.store.name)
    )
  }, [selectedMonth, stores])

  const selectedAvailableStores = useMemo(
    () => storeRows.filter(row => selectedStoreIds.has(row.store._id) && !row.disabled).map(row => row.store),
    [storeRows, selectedStoreIds]
  )

  const groupedStores = useMemo(() => {
    const map = new Map<string, StoreSelectionRow[]>()
    groups.forEach(group => {
      const groupStoreIds = new Set(group.stores)
      map.set(group._id, storeRows.filter(row => groupStoreIds.has(row.store._id)))
    })
    return map
  }, [storeRows, groups])

  const availableStoreIdsByGroup = useMemo(() => {
    const map = new Map<string, string[]>()
    groupedStores.forEach((groupStores, groupId) => {
      map.set(groupId, groupStores.filter(row => !row.disabled).map(row => row.store._id))
    })
    return map
  }, [groupedStores])

  const allStoreIdsInGroups = useMemo(() => {
    const ids = new Set<string>()
    groups.forEach(group => group.stores.forEach(storeId => ids.add(storeId)))
    return ids
  }, [groups])

  const storesWithoutGroup = useMemo(
    () => storeRows.filter(row => !allStoreIdsInGroups.has(row.store._id)),
    [storeRows, allStoreIdsInGroups]
  )

  const availableStoreIds = useMemo(
    () => storeRows.filter(row => !row.disabled).map(row => row.store._id),
    [storeRows]
  )
  const availableStoreIdSet = useMemo(() => new Set(availableStoreIds), [availableStoreIds])

  const allAvailableSelected = useMemo(
    () => availableStoreIds.length > 0 && availableStoreIds.every(id => selectedStoreIds.has(id)),
    [availableStoreIds, selectedStoreIds]
  )

  useEffect(() => {
    if (!selectedMonth || isCheckingAvailability) return

    setSelectedStores(currentSelectedStores => {
      const nextSelectedStores = currentSelectedStores.filter(storeId => availableStoreIdSet.has(storeId))
      return nextSelectedStores.length === currentSelectedStores.length ? currentSelectedStores : nextSelectedStores
    })
  }, [availableStoreIdSet, isCheckingAvailability, selectedMonth])

  useEffect(() => {
    if (!shouldScrollAfterSelectAll.current) return
    shouldScrollAfterSelectAll.current = false
    const timeout = window.setTimeout(
      () => window.requestAnimationFrame(() => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })),
      80
    )
    return () => window.clearTimeout(timeout)
  }, [selectedStores])

  const toggleStore = (storeId: string) => {
    if (isCheckingAvailability || hasLeftStep0 || !availableStoreIdSet.has(storeId)) return

    setSelectedStores(current =>
      current.includes(storeId)
        ? current.filter(selectedStore => selectedStore !== storeId)
        : [...current, storeId]
    )
  }

  const toggleAllStores = () => {
    if (isCheckingAvailability || hasLeftStep0) return

    if (allAvailableSelected) {
      setSelectedStores([])
      return
    }
    shouldScrollAfterSelectAll.current = true
    setSelectedStores(availableStoreIds)
  }

  const isGroupFullySelected = (groupId: string) => {
    const groupStoreIds = availableStoreIdsByGroup.get(groupId) || []
    return groupStoreIds.length > 0 && groupStoreIds.every(id => selectedStoreIds.has(id))
  }

  const isGroupPartiallySelected = (groupId: string) => {
    const groupStoreIds = availableStoreIdsByGroup.get(groupId) || []
    const selectedCount = groupStoreIds.filter(id => selectedStoreIds.has(id)).length
    return selectedCount > 0 && selectedCount < groupStoreIds.length
  }

  const toggleGroupStores = (groupId: string) => {
    if (isCheckingAvailability || hasLeftStep0) return

    const groupStoreIds = availableStoreIdsByGroup.get(groupId) || []
    if (groupStoreIds.length === 0) return

    if (isGroupFullySelected(groupId)) {
      const groupStoreIdSet = new Set(groupStoreIds)
      setSelectedStores(prev => prev.filter(id => !groupStoreIdSet.has(id)))
      return
    }
    setSelectedStores(prev => Array.from(new Set([...prev, ...groupStoreIds])))
  }

  const isGroupDisabled = (groupId: string) => {
    const groupStores = groupedStores.get(groupId) || []
    return groupStores.length > 0 && groupStores.every(store => store.disabled)
  }

  useEffect(() => {
    const loadStores = async () => {
      try {
        setStoresLoading(true)
        setStoresError(null)
        const { data } = await api.axios.get<BulkStore[]>('/store/bulk-goal-options')
        setStores(data)
      } catch {
        const message = 'Não foi possível carregar as lojas do usuário. Tente novamente.'
        setStoresError(message)
        toast.error(message)
      } finally {
        setStoresLoading(false)
      }
    }

    loadStores()
  }, [])

  useEffect(() => {
    const loadGroups = async () => {
      try {
        setGroupsLoading(true)
        setGroups((await api.axios.get<BulkGoalGroup[]>('/group')).data)
      } catch {
        const message = 'Não foi possível carregar os grupos de lojas. Tente novamente.'
        setStoresError(message)
        toast.error(message)
      } finally {
        setGroupsLoading(false)
      }
    }
    loadGroups()
  }, [])

  useEffect(() => {
    const cancelSource = axios.CancelToken.source()
    const requestId = availabilityRequestIdRef.current + 1
    availabilityRequestIdRef.current = requestId
    const isLatestAvailabilityRequest = () => availabilityRequestIdRef.current === requestId

    const checkAvailability = async () => {
      if (!selectedMonth || !storeIdsKey) {
        return
      }

      try {
        setIsCheckingAvailability(true)
        setStoresError(null)
        await new Promise(resolve => setTimeout(resolve, 0))
        const { data } = await api.axios.get<BulkStore[]>('/store/bulk-goal-options', {
          cancelToken: cancelSource.token,
          params: { month: selectedMonth }
        })

        if (!isLatestAvailabilityRequest()) return

        setStores(data)
      } catch (error) {
        if (axios.isCancel(error) || !isLatestAvailabilityRequest()) return

        const message = 'Não foi possível validar a disponibilidade das lojas. Tente novamente.'
        setStoresError(message)
        toast.error(message)
      } finally {
        if (isLatestAvailabilityRequest()) setIsCheckingAvailability(false)
      }
    }

    checkAvailability()

    return () => cancelSource.cancel()
  }, [selectedMonth, storeIdsKey])

  return {
    allAvailableSelected,
    availableStoreIds,
    groupedStores,
    groups,
    groupsLoading,
    hasLeftStep0,
    isCheckingAvailability,
    isGroupDisabled,
    isGroupFullySelected,
    isGroupPartiallySelected,
    selectedAvailableStores,
    selectedStoreIds,
    selectedStores,
    setHasLeftStep0,
    setSelectedStores,
    storesError,
    storesLoading,
    storesWithoutGroup,
    toggleAllStores,
    toggleGroupStores,
    toggleStore
  }
}
